import CustomLoader from "@/components/CustomLoader/customLoader.component";
import SetPattern from "@/components/SetPattern/setpattern.component";
import { ROUTE_LOGIN_OUT } from "@/constants/routes.constants";
import { INACTIVITY_TIMEOUT } from "@/constants/util.constants";
import AppProvider from "@/services/AppContext/appProvider";
import {
  expirePin,
  isPinExpired,
  isPinSet,
  isStoreValid,
} from "@/utils/pin.util";
import React, { useCallback, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import { Outlet, useNavigate } from "react-router";

const PinAuthentication: React.FC<{}> = () => {
  const [isPinValid, setIsPinValid] = useState(false);
  const [settingPin, setSettingPin] = useState(false);
  const [isPinCheckComplete, setIsPinCheckComplete] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const channel = new BroadcastChannel("pin_channel");
    channel.onmessage = (event) => {
      if (event.data === "pin_set") {
        setIsPinValid(true);
        setSettingPin(false);
      } else if (event.data === "pin_invalid") {
        setIsPinValid(false);
        setSettingPin(true);
      }
    };
    return () => {
      channel.close();
    };
  }, []);

  // Check if the pin is set and valid
  // If the pin is not set or expired, set the settingPin state to true
  const handlePinValidation = useCallback(async () => {
    if (settingPin) return;
    const pinSet = await isPinSet();
    if (!pinSet) {
      setIsPinValid(false);
      setSettingPin(true);
    } else {
      const pinExpired = await isPinExpired();
      if (pinExpired) {
        setIsPinValid(false);
        setSettingPin(true);
      } else {
        setIsPinValid(true);
      }
    }
    setIsPinCheckComplete(true);
  }, [settingPin]);

  // Check if the pin is set and valid on component mount
  useEffect(() => {
    handlePinValidation();
  }, [handlePinValidation]);

  // Inactivity Handlers
  const onIdle = useCallback(() => {
    setIsPinValid(false);
    expirePin();
  }, []);

  const onActive = () => {
    console.log("User is active");
  };

  const onAction = async () => {
    if (settingPin || !isPinValid) return;
    const validStore = await isStoreValid();
    if (!validStore) {
      nav(ROUTE_LOGIN_OUT);
    }
  };

  useIdleTimer({
    timeout: INACTIVITY_TIMEOUT,
    onIdle,
    onActive,
    onAction,
    debounce: 500,
    crossTab: true,
  });

  const handleMaxAttemptsReached = () => {
    nav(ROUTE_LOGIN_OUT);
  };

  const handlePinValidated = (isValid: boolean) => {
    setIsPinValid(isValid);
    if (isValid) {
      setSettingPin(false);
    }
  };

  if (!isPinCheckComplete) {
    return (
      <div className="container m-auto">
        <div className="flex h-screen items-center justify-center">
          <CustomLoader />
        </div>
      </div>
    );
  }

  return isPinValid ? (
    <AppProvider>
      <Outlet />
    </AppProvider>
  ) : (
    <SetPattern
      onPinValidated={handlePinValidated}
      onMaxAttemptsReached={handleMaxAttemptsReached}
    />
  );
};

export default PinAuthentication;
